import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { LivingLogoIcon } from "@/components/brand/LivingLogoIcon";
import { LivingLogoLED } from "@/components/brand/LivingLogoLED";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Heading, Text } from "@/components/ui/typography";
import { usePendingChatKitContext } from "@/features/assistant/context/ChatKitContext";
import { clearTokenCache, useChatKitToken } from "@/features/assistant/hooks/useChatKitToken";
import { useSession } from "@/hooks/useSession";
import { cn } from "@/lib/utils";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { semanticTokens } from "@packages/config/theme";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

/**
 * UserChatKitButton - Floating Action Button for authenticated user dashboard pages
 * Uses OpenAI ChatKit React SDK directly
 * 
 * Based on PublicChatKitButton but configured for authenticated users:
 * - isPublic: false (sends auth token)
 * - User-specific greeting and prompts
 * - Dashboard-optimized UI positioning
 * 
 * NOTE: @openai/chatkit-react handles its own loading/initialization.
 * We only need to provide getClientSecret for authentication.
 */
export function UserChatKitButton() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [userContextSent, setUserContextSent] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const isMountedRef = useRef(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { context: pendingContext, clearContext } = usePendingChatKitContext();
  const session = useSession();

  // Detect mobile for bottom sheet on mobile, side panel on desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Extract user name from email (before @) for personalized greeting
  const userName = useMemo(() => {
    if (session.status === 'authed' && session.user?.email) {
      const namePart = session.user.email.split('@')[0];
      // Capitalize first letter
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    }
    return null;
  }, [session]);

  // Use the token management hook - isPublic: false for authenticated users
  const { getClientSecret } = useChatKitToken({
    path: location.pathname,
    isPublic: false, // Key difference: authenticated user context
    onTokenFetched: () => {
      if (isMountedRef.current) {
        setError(null);
      }
    },
    onTokenError: (err) => {
      if (isMountedRef.current) {
        setError(err.message);
      }
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Focus trap: handle Escape key to close
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus the dialog when opened (accessibility)
  useEffect(() => {
    if (open && dialogRef.current) {
      const focusable = dialogRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }
  }, [open]);

  // Listen for open events from other components (e.g., Help Center)
  useEffect(() => {
    const handleOpen = () => {
      setOpen(true);
    };

    window.addEventListener('chatkit:open', handleOpen);
    return () => {
      window.removeEventListener('chatkit:open', handleOpen);
    };
  }, []);

  // Memoize ChatKit config to prevent unnecessary re-renders
  // User-specific prompts for dashboard context with personalized greeting
  const chatKitConfig = useMemo(() => ({
    api: { getClientSecret },
    locale: (typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en') as 'en' | 'ru' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja' | 'ko' | 'zh' | undefined,
    theme: 'light' as const,
    startScreen: {
      greeting: userName
        ? `Hi ${userName}! 👋 I'm your Supabank AI assistant. How can I help you today?`
        : 'Hi! I\'m your Supabank AI assistant. How can I help you today?',
      prompts: [
        { label: 'Check my balance', prompt: 'What is my current account balance?' },
        { label: 'Transaction history', prompt: 'Show me my recent transactions' },
        { label: 'Deposit crypto', prompt: 'How do I deposit cryptocurrency?' },
        { label: 'Withdrawal help', prompt: 'How do I withdraw funds to my bank?' },
        { label: 'Verify account', prompt: 'How do I complete KYC verification?' },
        { label: 'Contact support', prompt: 'I need to speak with a support agent' },
      ],
    },
    composer: {
      placeholder: 'Ask me anything about your account...',
    },
    onError: ({ error: chatKitError }: { error: Error }) => {
      console.error('[UserChatKitButton] ChatKit error:', chatKitError);
      if (isMountedRef.current) {
        setError('Connection error. Please try again.');
      }
    },
  }), [getClientSecret, userName]);

  // Initialize ChatKit with memoized config - SDK handles all loading internally
  const chatkit = useChatKit(chatKitConfig);

  // Send user_id context as the first message when ChatKit opens (required for agent tools)
  useEffect(() => {
    const canSend = typeof chatkit.control?.sendUserMessage === 'function';
    if (open && !userContextSent && session.status === 'authed' && session.user?.id && chatkit.control && canSend) {
      const timer = setTimeout(() => {
        try {
          chatkit.control.sendUserMessage({
            text: `[SYSTEM: user_id=${session.user!.id}]`
          });
          setUserContextSent(true);
        } catch (error) {
          console.error('[UserChatKitButton] Failed to send user context:', error);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open, userContextSent, session, chatkit.control]);

  // Reset userContextSent when ChatKit closes
  useEffect(() => {
    if (!open) {
      setUserContextSent(false);
    }
  }, [open]);

  // Send initial message if pending context exists (from Help Center articles)
  useEffect(() => {
    const canSend = typeof chatkit.control?.sendUserMessage === 'function';
    if (open && pendingContext?.initialMessage && chatkit.control && userContextSent && canSend) {
      const timer = setTimeout(() => {
        try {
          chatkit.control.sendUserMessage({ text: pendingContext.initialMessage! });
          clearContext();
        } catch (error) {
          console.error('[UserChatKitButton] Failed to send initial message:', error);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [open, pendingContext, chatkit.control, clearContext, userContextSent]);

  // Retry handler - clears cache
  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    setError(null);
    clearTokenCache();

    // Reset by closing and reopening
    if (retryCount < 2) {
      setOpen(false);
      setTimeout(() => setOpen(true), 200);
    } else {
      setError('Unable to connect. Please try again later or contact support.');
    }
  }, [retryCount]);

  return (
    <>
      {/* Floating Action Button - Desktop only (hidden on mobile where SupaFAB provides access) */}
      {/* Hidden when dialog is open to avoid aria-hidden conflict */}
      {!open && (
        <div
          className="fixed right-6 z-[9999] hidden lg:block"
          style={{
            // Desktop only: position above footer
            bottom: "calc(var(--footer-height, 56px) + var(--safe-area-bottom, 0px) + 1rem)",
          }}
        >
          <div className="relative">
            <Button
              size="lg"
              onClick={() => setOpen(true)}
              className={cn(
                // Canonical FAB size: 64×64px (h-16 w-16)
                "h-16 w-16 rounded-full shadow-lg hover:shadow-xl",
                semanticTokens.motion.standard,
                "touch-manipulation",
                "pointer-events-auto cursor-pointer",
                semanticTokens.focus.ring,
                // Canonical FAB style
                "bg-gradient-to-br from-primary to-primary/90",
                "backdrop-blur-sm",
                "hover:from-primary/95 hover:to-primary/85",
                semanticTokens.fg.inverted,
                "border border-white/10",
                // Enhanced shadow with glow effect
                "shadow-lg shadow-primary/25",
                "hover:shadow-xl hover:shadow-primary/30",
              )}
              aria-label="Open AI Support Assistant"
              aria-describedby="user-assistant-button-description"
              data-testid="user-chatkit-button"
            >
              <LivingLogoIcon size={56} variant="inverted" showEnergyFlow={true} showLed={false} />
              <span id="user-assistant-button-description" className="sr-only">
                Opens a dialog with the AI assistant for help with your account
              </span>
            </Button>
            {/* LED micro-dot - Outside button contour, bottom right */}
            <LivingLogoLED logoSize={56} className="z-10" show={true} />
          </div>
        </div>
      )}

      {/* ChatKit Dialog - Bottom sheet on mobile (like modern platforms), side panel on desktop */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          ref={dialogRef}
          className={cn(
            "flex w-full flex-col gap-0 p-0",
            // Desktop: side panel
            "lg:w-[32rem] lg:max-w-2xl",
            semanticTokens.bg.surface,
          )}
          style={{
            // Mobile: bottom sheet height (85vh - like internal browser)
            // Desktop: full height side panel
            height: isMobile ? '85dvh' : '100dvh',
            maxHeight: isMobile ? '85dvh' : '100dvh',
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* A11y: Hidden title and description for screen readers */}
          <VisuallyHidden>
            <SheetTitle>AI Support Assistant</SheetTitle>
            <SheetDescription>Chat with Supabank AI Assistant for help with your account and transactions</SheetDescription>
          </VisuallyHidden>

          {/* Header */}
          <div
            className={cn(
              "flex flex-row items-center justify-between border-b",
              "px-4 py-3 sm:px-6 sm:py-4",
              semanticTokens.border.default,
              semanticTokens.bg.surface,
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full",
                "bg-gradient-to-br from-primary to-primary/80"
              )}>
                <LivingLogoIcon
                  size={24}
                  variant="inverted"
                  showEnergyFlow={true}
                  showLed={true}
                />
              </div>
              <div>
                <Heading level={5}>
                  AI Support
                </Heading>
                <Text variant="caption" color="muted">
                  Here to help with your account
                </Text>
              </div>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="flex h-full items-center justify-center p-6">
              <div className="text-center space-y-4 max-w-sm">
                <div className={cn(
                  "w-12 h-12 mx-auto rounded-full flex items-center justify-center",
                  semanticTokens.warning.bg
                )}>
                  <LivingLogoIcon size={24} variant="light" showEnergyFlow={false} showLed={false} />
                </div>
                <div>
                  <p className={cn("text-sm font-semibold mb-1", semanticTokens.fg.default)}>
                    Connection Error
                  </p>
                  <p className={cn("text-xs", semanticTokens.fg.muted)}>
                    {error}
                  </p>
                </div>
                <Button
                  onClick={handleRetry}
                  size="sm"
                  variant="outline"
                  disabled={retryCount >= 3}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  {retryCount >= 3 ? 'Contact Support' : 'Try Again'}
                </Button>
              </div>
            </div>
          )}

          {/* ChatKit widget - SDK handles all loading/error states internally */}
          {!error && (
            <div
              className={cn("flex-1 overflow-hidden", semanticTokens.bg.surface)}
              style={{
                minHeight: 0,
                flex: '1 1 auto',
              }}
            >
              <ChatKit
                control={chatkit.control}
                className="h-full w-full"
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export default UserChatKitButton;
